import { adjust } from "../../utils/dice";
import type { LiveSheetAttribute, LiveSheetSpellRoll } from "../../types/generated/live";
import type { RollSpec } from "../ui/roll_chip";
import { type Dict, interpolate, type Labels } from "./labels";

const DESCRIPTION_LIMIT = 255; // DiceRoll validates the column at 255

// `d20+3`, `1d8-1`, `1d6`: a flat number after the dice, none when it is zero.
export function withFlat(dice: string, flat: number): string {
	if (flat === 0) return dice;
	return `${dice}${flat > 0 ? "+" : "-"}${Math.abs(flat)}`;
}

// A test rolled under a score: a die (the d20, or the Ladrão's d6) that succeeds up to it.
export const attributeRoll = (attribute: LiveSheetAttribute, labels: Pick<Labels, "descriptions" | "attribute_names">): RollSpec | null =>
	attribute.score == null ? null : testRoll(interpolate(labels.descriptions.test_of, { attribute: labels.attribute_names[attribute.key] }), attribute.score);

export const testRoll = (description: string, target: number, dice = "1d20"): RollSpec => ({ instruction: dice, success: `1-${target}`, description });

// The most a Fácil/Difícil bonus moves an agonize test, as `SheetWrites` clamps it.
const AGONIZE_LIMIT = 10;

export const clampAdjustment = (bonus: number) => Math.max(-AGONIZE_LIMIT, Math.min(AGONIZE_LIMIT, bonus));

export const agonizeRoll = (description: string, target: number): RollSpec => ({ ...testRoll(description, target), limit: AGONIZE_LIMIT });

export const attackRoll = (bonus: number, description: string): RollSpec => ({ instruction: withFlat("d20", bonus), attack: true, description });

// BAC and BAD are attacks worded as an attack row's, the wording the feed tells an attack by (`DiceRoll::ATTACK_DESCRIPTION`).
export function baseAttackRolls({ bac, bad }: { bac: number; bad: number }, { descriptions, terms }: Pick<Labels, "descriptions" | "terms">) {
	return {
		melee: attackRoll(bac, interpolate(descriptions.attack, { weapon: terms.attack_bonus_melee.full })),
		ranged: attackRoll(bad, interpolate(descriptions.ranged_attack, { weapon: terms.attack_bonus_ranged.full })),
	};
}

export const plainRoll = (instruction: string, description: string): RollSpec => ({ instruction, description });

// An adjustment is worded ahead of the description, which the feed and the toast cut short.
function describeAdjustment(description: string, bonus: number, label: string | undefined, names: Dict): string {
	if (!label) return description;
	const signed = bonus > 0 ? `+${bonus}` : `-${Math.abs(bonus)}`;
	const prefix = `(${interpolate(names[label] || "%{bonus}", { bonus: signed })})`;
	const base = description.slice(0, DESCRIPTION_LIMIT - prefix.length - 1).trim();
	return base ? `${prefix} ${base}` : prefix;
}

// The pending Fácil/Difícil bonus applied as the dice bar does (`adjust`), with the description saying so.
export function adjusted(spec: RollSpec, bonusAsked: number, names: Dict) {
	const bonus = spec.limit === undefined ? bonusAsked : Math.max(-spec.limit, Math.min(spec.limit, bonusAsked));
	const { instruction, success, label } = adjust({ instruction: spec.instruction, success: spec.success, bonus, attack: spec.attack });
	return {
		formula: instruction as string,
		success: success as string | undefined,
		attack: spec.attack,
		description: describeAdjustment(spec.description, bonus, label, names),
	};
}

// The spell's own label when it defines one, else the sheet's word for the kind of roll.
export function rollLabel(roll: LiveSheetSpellRoll, labels: Labels): string {
	return roll.label ?? labels.spells[`roll_${roll.kind}`] ?? "";
}
